import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get, omit, compact } from 'lodash-es';
import classnames from 'classnames';
import { AdUnit } from 'site-modules/shared/components/ad-unit/ad-unit';
import { updateVehicleByStyleId } from 'client/actions/vehicle';
import { queryToSearch } from 'client/utils/query-to-search';
import { getQuery } from 'client/utils/location';
import { venomHistory } from 'client/utils/history/venom-history';
import { parseVehicleParam } from 'client/utils/parse-vehicle-param';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { convertSubmodelStyleToStyle, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { normalizeQueryOrder } from 'site-modules/comparator/utils/query-handler';
import { buildVehiclePathFromColumns } from 'site-modules/comparator/utils/comparator';
import { delayHeavyTask } from 'site-modules/shared/utils/inp-utils';

// Constants
import {
  COMPARATOR_PATHNAME,
  MAX_COMPARE_SIZE,
  NAVIGATION_MENU_HEIGHT,
  VEHICLE_PATH_NAME,
} from 'site-modules/comparator/constants/comparator';

// Components
import { FloodlightAd } from 'site-modules/shared/components/floodlight/floodlight-ad';
import { comparatorGridStickyScroll } from 'site-modules/comparator/components/comparator-grid-sticky-scroll/comparator-grid-sticky-scroll';
import { ComparatorTable } from 'site-modules/comparator/components/comparator-table/comparator-table';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import './comparator-grid.scss';

export function ComparatorGridContent({
  veh1 = null,
  veh2 = null,
  veh3 = null,
  veh4 = null,
  title,
  columns,
  navList,
  sponsoredContent = null,
  deviceName,
  onStickyStateChange,
  comparatorHeaderRef,
  comparatorHeaderStickyRef,
  setCustomOffset,
  customOffset = 0,
  isMobile = false,
  hasComparisonContent = true,
  onUpdateVehicle,
  location = {
    search: '',
    pathname: '',
  },
  isMMvsMMComparator = false,
  hideSimilar = false,
  isCore6982Enabled = false,
}) {
  const [sponsoredColumnRemoved, setSponsoredColumnRemoved] = useState(false);
  const styles = [veh1, veh2, veh3, veh4];

  const queryForMMvsMMComparator = useMemo(() => {
    return [veh1, veh2, veh3, veh4].filter(Boolean).reduce((acc, veh, index) => {
      acc[`${VEHICLE_PATH_NAME}${index + 1}`] = veh.id;
      return acc;
    }, {});
  }, [veh1, veh2, veh3, veh4]);

  const removeVehicle = useCallback(
    ({ name }) => {
      const query = isMMvsMMComparator ? queryForMMvsMMComparator : getQuery(location);
      const newQuery = omit(query, name);
      const newState = normalizeQueryOrder(newQuery, COMPARATOR_PATHNAME);

      if (name === 'sponsor') {
        setSponsoredColumnRemoved(true);
      }

      if (query.veh1 !== newQuery.veh1) {
        const { styleId } = parseVehicleParam(newState.query, 'veh1');
        onUpdateVehicle(styleId);
      }

      delayHeavyTask(() => {
        venomHistory.replace({
          ...newState,
          search: queryToSearch(newState.query),
          query: undefined,
        });
      });
    },
    [isMMvsMMComparator, location, onUpdateVehicle, queryForMMvsMMComparator]
  );

  const addVehicle = useCallback(
    ({ styleId, submodel, name }) => {
      const query = getQuery(location);
      let newName = null;

      if (name === 'sponsor') {
        removeVehicle({ name });
        newName = `${VEHICLE_PATH_NAME}${MAX_COMPARE_SIZE}`;
      }

      const newQuery = {
        ...query,
        ...(isMMvsMMComparator && queryForMMvsMMComparator),
        [newName || name]: compact([styleId, submodel]).join('|'),
      };

      const newState = {
        pathname: COMPARATOR_PATHNAME,
        search: queryToSearch(newQuery),
      };

      if (query.veh1 !== newQuery.veh1) {
        onUpdateVehicle(styleId);
      }

      delayHeavyTask(() => {
        venomHistory.replace(newState);
      });
    },
    [isMMvsMMComparator, location, onUpdateVehicle, queryForMMvsMMComparator, removeVehicle]
  );

  useEffect(() => {
    if (comparatorHeaderRef.current) {
      setCustomOffset(comparatorHeaderRef.current.clientHeight + NAVIGATION_MENU_HEIGHT);
    }
  });

  const hasSponsoredColumn = sponsoredContent && !sponsoredColumnRemoved;
  const columnsWithSelection = columns.filter(column => column.styleId).length;
  const shouldRenderSelected = !!columnsWithSelection || hasSponsoredColumn;
  const firstColumnVehicle = get(columns, '[0]', {});
  const shouldRenderFloodlight = !!firstColumnVehicle.styleId;

  const columnsToRender = [...columns];

  if (hasSponsoredColumn) {
    const sponsoredQuery = {
      sponsor: sponsoredContent.styleId,
    };
    const sponsoredColumn = {
      ...parseVehicleParam(sponsoredQuery, 'sponsor'),
      ...get(sponsoredContent, `${deviceName}`),
    };
    sponsoredColumn.oemLogoSrc = get(sponsoredContent, 'oemLogoSrc');
    sponsoredColumn.isSponsored = true;

    if (sponsoredColumn.styleId) {
      columnsToRender[columnsToRender.length - 1] = sponsoredColumn;
    }
  }

  return (
    <div className={classnames('car-comparator-grid', { sponsored: hasSponsoredColumn })}>
      <ComparatorTable
        columns={columnsToRender}
        styles={styles}
        navList={navList}
        customOffset={customOffset}
        addVehicle={addVehicle}
        removeVehicle={removeVehicle}
        onStickyStateChange={onStickyStateChange}
        title={title}
        sponsoredContent={get(sponsoredContent, `${deviceName}`)}
        shouldRenderSelected={shouldRenderSelected}
        comparatorHeaderRef={comparatorHeaderRef}
        comparatorHeaderStickyRef={comparatorHeaderStickyRef}
        isSmall={isMobile}
        hasComparisonContent={hasComparisonContent}
        hideSimilar={hideSimilar}
        isCore6982Enabled={isCore6982Enabled}
      />
      {hasSponsoredColumn && (
        <AdUnit adName="compare" position={'1'} all renderWhenViewable={false} refreshable={false} />
      )}
      {shouldRenderFloodlight && <FloodlightAd />}
      {styles[0] && (
        <div className="mt-1">
          <Experiment name="ads-12460_dfp" showDefault>
            <Recipe name="ctrl" isDefault>
              <MinHeightWrapper componentType="SPONSORED_CARS">
                <SponsoredCars />
              </MinHeightWrapper>
            </Recipe>
            <Recipe name="chal">
              <MinHeightWrapper componentType="SPONSORED_CARS_STACKED" style={isMobile ? null : { maxWidth: '744px' }}>
                <SponsoredCars showStackedAds />
              </MinHeightWrapper>
            </Recipe>
          </Experiment>
        </div>
      )}
    </div>
  );
}

ComparatorGridContent.propTypes = {
  onStickyStateChange: PropTypes.func.isRequired,
  comparatorHeaderRef: PropTypes.shape().isRequired,
  comparatorHeaderStickyRef: PropTypes.shape().isRequired,
  onUpdateVehicle: PropTypes.func.isRequired,
  setCustomOffset: PropTypes.func.isRequired,
  deviceName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      styleId: PropTypes.string,
    })
  ).isRequired,
  veh1: VehicleEntities.Style,
  veh2: VehicleEntities.Style,
  veh3: VehicleEntities.Style,
  veh4: VehicleEntities.Style,
  navList: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, hash: PropTypes.string })).isRequired,
  customOffset: PropTypes.number,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  sponsoredContent: PropTypes.shape({
    styleId: PropTypes.string,
    oemLogoSrc: PropTypes.string,
    mobile: PropTypes.shape({
      oemLogoLink: PropTypes.string,
      oemYmmLink: PropTypes.string,
      oemGetStartedLink: PropTypes.string,
      oemDealerInventoryLink: PropTypes.string,
      oemConsumerReviewLink: PropTypes.string,
      oemEdmundsReviewLink: PropTypes.string,
    }),
    desktop: PropTypes.shape({
      oemLogoLink: PropTypes.string,
      oemYmmLink: PropTypes.string,
      oemGetStartedLink: PropTypes.string,
      oemDealerInventoryLink: PropTypes.string,
      oemConsumerReviewLink: PropTypes.string,
      oemEdmundsReviewLink: PropTypes.string,
    }),
  }),
  isMobile: PropTypes.bool,
  hideSimilar: PropTypes.bool,
  isMMvsMMComparator: PropTypes.bool,
  hasComparisonContent: PropTypes.bool,
  isCore6982Enabled: PropTypes.bool,
};

const stateToPropsConfig = {
  veh1: bindToPath(({ columns }) => buildVehiclePathFromColumns(columns, 0), VehicleModel, convertSubmodelStyleToStyle),
  veh2: bindToPath(({ columns }) => buildVehiclePathFromColumns(columns, 1), VehicleModel, convertSubmodelStyleToStyle),
  veh3: bindToPath(({ columns }) => buildVehiclePathFromColumns(columns, 2), VehicleModel, convertSubmodelStyleToStyle),
  veh4: bindToPath(({ columns }) => buildVehiclePathFromColumns(columns, 3), VehicleModel, convertSubmodelStyleToStyle),
};

export const ComparatorGrid = connect(null, { onUpdateVehicle: updateVehicleByStyleId })(
  connectToModel(comparatorGridStickyScroll(ComparatorGridContent), stateToPropsConfig)
);
